/**
 * Portfólio de CLIENTES da Agência VPC.
 * Núcleo real: ecossistema R3 Group LLC do Marcio.
 * Editável — cada cliente vira filtro nas demandas e contexto pros prompts da IA.
 */

export type ClientSegment =
  | "turismo-luxo" | "infoproduto" | "fintech" | "saas-b2b"
  | "saas-criativo" | "saas-conversacional" | "holding" | "lifestyle";

export type ClientStatus = "ativo" | "onboarding" | "pausado" | "prospect";

export interface BrandKit {
  primary: string;    // cor primária
  secondary: string;
  accent: string;
  /** url de logo opcional (data url ou http) */
  logoUrl?: string;
  /** fonte preferida (família) */
  font?: string;
}

export type ClientKind = "client" | "tool";

export interface Client {
  id: string;
  name: string;
  shortName: string;
  segment: ClientSegment;
  status: ClientStatus;
  /** "client" = empresa cliente externa; "tool" = ferramenta interna criada pela agência */
  kind?: ClientKind;
  /** descrição em 1-2 frases */
  pitch: string;
  /** público-alvo (1 frase) */
  audience: string;
  /** desafio principal */
  challenge: string;
  /** ticket médio em USD/mês de fee da agência */
  fee: number;
  /** prioridade no portfólio */
  priority: "core" | "growth" | "satellite";
  /** site / referência principal (URL pública da ferramenta/empresa) */
  url?: string;
  /** repositório GitHub do projeto (pra ferramentas internas) */
  githubUrl?: string;
  /** link pro projeto no Claude Playground (claude.ai/projects ou similar) */
  claudePlaygroundUrl?: string;
  /** brand kit visual */
  brand: BrandKit;
  /** tags pra busca/IA */
  tags: string[];
  /** desde quando é cliente */
  since: string;
}

export const CLIENTS: Client[] = [
  // --------- HOLDING ---------
  {
    id: "r3-group",
    name: "R3 Group LLC",
    shortName: "R3 Group",
    segment: "holding",
    status: "ativo",
    pitch: "Holding mãe — guarda-chuva dos negócios do Marcio. Marca corporativa internacional.",
    audience: "Investidores, parceiros institucionais, governo americano.",
    challenge: "Costurar narrativa única entre operações tão diferentes (rental, fintech, mentoria, SaaS).",
    fee: 12000,
    priority: "core",
    url: "r3group.com",
    brand: { primary: "#0B1F3A", secondary: "#C9A14A", accent: "#F2EEE6", font: "serif clássica" },
    tags: ["holding", "corporativo", "institucional", "EUA"],
    since: "2024-01"
  },

  // --------- TURISMO LUXO ---------
  {
    id: "r3-rental",
    name: "R3 Rental Car",
    shortName: "R3 Rental",
    segment: "turismo-luxo",
    status: "ativo",
    pitch: "Locação de veículos premium em Orlando — frota selecionada, atendimento em português, experiência de luxo discreto.",
    audience: "Turista brasileiro classe A/A+ em Disney/Orlando, executivos em trip de negócios.",
    challenge: "Subir o ticket médio e o LTV mantendo a percepção de exclusividade.",
    fee: 18000,
    priority: "core",
    url: "r3rental.com",
    brand: { primary: "#1A1A1A", secondary: "#C9A14A", accent: "#F8F6F1", font: "sans premium" },
    tags: ["rental", "luxo", "orlando", "turismo", "FL", "BR-US"],
    since: "2024-03"
  },

  // --------- INFOPRODUTO ---------
  {
    id: "mentoria-vpc",
    name: "Mentoria VPC — Vou para o Castelo",
    shortName: "Mentoria VPC",
    segment: "infoproduto",
    status: "ativo",
    pitch: "Mentoria de marketing digital e estratégia de empresário. 'Vou para o Castelo' é o método de escala do Marcio.",
    audience: "Empresários BR de 6-7 dígitos que querem internacionalizar e estruturar holding.",
    challenge: "Lançamento perpétuo + clássico — escalar sem perder o filtro de aluno premium.",
    fee: 22000,
    priority: "core",
    url: "vouparaocastelo.com.br",
    brand: { primary: "#7E1A1F", secondary: "#D4AF37", accent: "#1A1410", font: "serif editorial" },
    tags: ["mentoria", "infoproduto", "lançamento", "empresário", "premium-BR"],
    since: "2024-06"
  },

  // --------- FINTECH ---------
  {
    id: "genesis",
    name: "Genesis Finance Protection",
    shortName: "Genesis",
    segment: "fintech",
    status: "ativo",
    pitch: "Proteção patrimonial e estruturação financeira internacional para brasileiros nos EUA.",
    audience: "Brasileiros com patrimônio nos EUA — investidores, empresários, expats.",
    challenge: "Educar mercado BR sobre proteção patrimonial sem soar nem fintech genérica, nem seguradora.",
    fee: 16000,
    priority: "core",
    url: "genesisfinance.us",
    brand: { primary: "#0F3B2E", secondary: "#C9A14A", accent: "#F2EEE6", font: "sans corporativa" },
    tags: ["fintech", "insurance", "proteção", "patrimônio", "BR-US"],
    since: "2024-09"
  },

  // --------- SaaS B2B ---------
  {
    id: "extratos-ia",
    name: "Extratos IA",
    shortName: "Extratos IA",
    segment: "saas-b2b",
    status: "ativo",
    kind: "tool",
    githubUrl: "https://github.com/megamarcio/extratos-ia",
    claudePlaygroundUrl: "https://claude.ai/projects",
    pitch: "Plataforma que lê extratos bancários US/CA/UK/EU via Plaid e gera análise financeira com Claude.",
    audience: "Contadores, family offices, advisors que processam extratos em escala.",
    challenge: "Posicionar como ferramenta de elite, não comoditizar como 'mais um SaaS de IA'.",
    fee: 8000,
    priority: "growth",
    url: "extrato.familycava.com",
    brand: { primary: "#0A0A0A", secondary: "#3DB97E", accent: "#F4F4F4", font: "mono técnica" },
    tags: ["saas", "fintech", "AI", "contabilidade", "B2B"],
    since: "2025-02"
  },

  // --------- SaaS CRIATIVO ---------
  {
    id: "print-art",
    name: "Print Art",
    shortName: "Print Art",
    segment: "saas-criativo",
    status: "ativo",
    kind: "tool",
    url: "https://printart.cchub.com.br",
    githubUrl: "https://github.com/megamarcio/print-art",
    claudePlaygroundUrl: "https://claude.ai/projects",
    pitch: "Gerador automatizado de prints e artes para sites, marketplaces e Pinterest.",
    audience: "Vendedores de print-on-demand, marketplaces (Redbubble/Etsy), criadores solo.",
    challenge: "Diferenciar de geradores genéricos — entregar curadoria + escala.",
    fee: 6000,
    priority: "growth",
    brand: { primary: "#1B1B1B", secondary: "#FF6B35", accent: "#FFFFFF", font: "sans display" },
    tags: ["saas", "criativo", "print-on-demand", "automação"],
    since: "2025-03"
  },

  // --------- SaaS CONVERSACIONAL ---------
  {
    id: "r3-aurelia",
    name: "R3 Aurelia",
    shortName: "Aurelia",
    segment: "saas-conversacional",
    status: "ativo",
    kind: "tool",
    githubUrl: "https://github.com/megamarcio/r3-aurelia",
    claudePlaygroundUrl: "https://claude.ai/projects",
    pitch: "Atendente IA via WhatsApp para empresas — voz humana, integração com CRM, lembra de contexto.",
    audience: "Pequenas e médias empresas BR que atendem por WhatsApp e querem escalar sem perder humanização.",
    challenge: "Convencer dono de PME que IA conversacional não soa robô. Provar via teste grátis 7 dias.",
    fee: 9000,
    priority: "growth",
    brand: { primary: "#2D1B69", secondary: "#FFCB05", accent: "#F8F8F8", font: "sans humanista" },
    tags: ["whatsapp", "AI", "SaaS", "atendimento", "conversacional"],
    since: "2025-01"
  },

  // --------- LIFESTYLE FAMILIAR ---------
  {
    id: "family-cava",
    name: "Family Cava",
    shortName: "Family Cava",
    segment: "lifestyle",
    status: "ativo",
    kind: "tool",
    url: "https://familycava.com",
    githubUrl: "https://github.com/megamarcio/family-cava",
    claudePlaygroundUrl: "https://claude.ai/projects",
    pitch: "Marca lifestyle/wine cellar da família — venda direta para amigos e rede próxima.",
    audience: "Círculo de relacionamento Marcio + clientes premium de outros negócios (cross-sell).",
    challenge: "Manter intimidade sem virar 'mais uma marca de wine'. Posicionamento boutique.",
    fee: 4000,
    priority: "satellite",
    brand: { primary: "#3D1A1F", secondary: "#C9A14A", accent: "#F1E9D9", font: "serif elegante" },
    tags: ["lifestyle", "wine", "boutique", "familiar"],
    since: "2024-12"
  },

  // --------- PROSPECT (não fechado ainda) ---------
  {
    id: "telegram-org",
    name: "Telegram Organizer",
    shortName: "TG Organizer",
    segment: "saas-b2b",
    status: "prospect",
    kind: "tool",
    githubUrl: "https://github.com/megamarcio/telegram-organizer",
    pitch: "Ferramenta de organização de canais e grupos do Telegram para criadores e empresas.",
    audience: "Criadores de conteúdo BR com canais grandes no Telegram.",
    challenge: "Validar mercado antes de investir em marca + tráfego.",
    fee: 3000,
    priority: "satellite",
    brand: { primary: "#0088CC", secondary: "#1F1F1F", accent: "#FFFFFF", font: "mono" },
    tags: ["saas", "telegram", "creator", "prospect"],
    since: "2025-05"
  },

  // --------- VPC INSTA (ferramenta) ---------
  {
    id: "vpc-insta",
    name: "VPC Insta",
    shortName: "VPC Insta",
    segment: "saas-criativo",
    status: "ativo",
    kind: "tool",
    url: "https://insta.vouparaocastelo.com.br",
    githubUrl: "https://github.com/megamarcio/vpc-insta",
    claudePlaygroundUrl: "https://claude.ai/projects",
    pitch: "Plataforma de publicação automatizada de carrosséis Instagram pras 6 contas IG da casa — Meta Graph API + Nano Banana Pro.",
    audience: "Equipe interna VPC + Endomarketing. Posta sem login manual, sem mexer no celular.",
    challenge: "Manter tokens long-lived renovados, queue de publicação confiável, agendamento.",
    fee: 0,
    priority: "core",
    brand: { primary: "#E1306C", secondary: "#1F1F1F", accent: "#F8F8F8", font: "sans display" },
    tags: ["saas", "instagram", "publicacao", "automacao", "interno"],
    since: "2026-04"
  },

  // --------- KOMMO BOT (ferramenta) ---------
  {
    id: "kommo-bot",
    name: "Kommo Bot",
    shortName: "Kommo Bot",
    segment: "saas-conversacional",
    status: "ativo",
    kind: "tool",
    url: "https://r3rentalcar.kommo.com",
    githubUrl: "https://github.com/megamarcio/kommo-mcp",
    claudePlaygroundUrl: "https://claude.ai/projects",
    pitch: "MCP + bot que conecta Claude diretamente ao CRM Kommo — cria leads, move pelo funil, gera mensagens contextuais.",
    audience: "Equipe de Vendas R3/VPC + agentes IA do funil 'Vou para o Castelo'.",
    challenge: "Sincronizar o estado do funil entre Claude/Aurelia/Marcio sem duplicar trabalho.",
    fee: 0,
    priority: "core",
    brand: { primary: "#23A6F0", secondary: "#1B2B3B", accent: "#F4F8FB", font: "sans humanista" },
    tags: ["mcp", "crm", "kommo", "automacao", "interno"],
    since: "2026-03"
  },

  // --------- KAST (ferramenta / site) ---------
  {
    id: "kast",
    name: "Kast",
    shortName: "Kast",
    segment: "saas-b2b",
    status: "ativo",
    kind: "tool",
    url: "https://kast.cchub.com.br",
    githubUrl: "https://github.com/megamarcio/kast",
    claudePlaygroundUrl: "https://claude.ai/projects",
    pitch: "Site/plataforma Kast — produto recente da casa. Construído com Claude Code, em produção via CCHub.",
    audience: "Definir conforme público-alvo final.",
    challenge: "Validar mercado, refinar posicionamento, escalar tráfego.",
    fee: 0,
    priority: "growth",
    brand: { primary: "#1A1A1A", secondary: "#FFD23F", accent: "#FFFFFF", font: "sans display" },
    tags: ["saas", "kast", "produto-novo", "interno"],
    since: "2026-05"
  }
];

export const clientById = (id: string) => CLIENTS.find(c => c.id === id);

export const SEGMENT_LABEL: Record<ClientSegment, string> = {
  "turismo-luxo":         "Turismo · Luxo",
  "infoproduto":          "Infoproduto",
  "fintech":              "Fintech",
  "saas-b2b":             "SaaS B2B",
  "saas-criativo":        "SaaS Criativo",
  "saas-conversacional":  "SaaS Conversacional",
  "holding":              "Holding",
  "lifestyle":            "Lifestyle"
};

export const STATUS_LABEL: Record<ClientStatus, string> = {
  "ativo":      "ATIVO",
  "onboarding": "ONBOARDING",
  "pausado":    "PAUSADO",
  "prospect":   "PROSPECT"
};
